import * as React from "react";

import type {AudioCache} from "@khan/guide-core-helpers";

import {GuideAudioProvider} from "./internal/guide-audio-provider.tsx";
import {GuideConversionContextProvider} from "./internal/guide-conversion-context-provider.tsx";
import {GuidePreferencesProvider} from "./internal/guide-preferences-provider.tsx";
import {GuideStateApiProvider} from "./internal/guide-state-api-provider.tsx";
import {MathEditorContextProvider} from "./internal/math-editor-context-provider.tsx";
import {MobileIconMenuContextProvider} from "./internal/mobile-icon-menu-context-provider.tsx";
import {NotificationStateProvider} from "./notification-state-provider.tsx";

interface Props {
    children: React.ReactNode;
    initialAudioCache?: AudioCache;
    /**
     * Optional target user KAID to fetch Khanmigo preferences for.
     * If not provided, fetches preferences for the currently logged-in user.
     * This is useful for preview modes where a teacher wants to view a student's
     * Khanmigo settings.
     */
    targetKaid?: string;
}

/**
 * Provides the guide context to the application.
 */
const GuideContextProvider = ({
    children,
    initialAudioCache,
    targetKaid,
}: Props): React.ReactElement => (
    <GuideStateApiProvider>
        <MathEditorContextProvider>
            <MobileIconMenuContextProvider>
                <GuideConversionContextProvider>
                    <GuidePreferencesProvider targetKaid={targetKaid}>
                        <GuideAudioProvider
                            initialAudioCache={initialAudioCache}
                        >
                            <NotificationStateProvider>
                                {children}
                            </NotificationStateProvider>
                        </GuideAudioProvider>
                    </GuidePreferencesProvider>
                </GuideConversionContextProvider>
            </MobileIconMenuContextProvider>
        </MathEditorContextProvider>
    </GuideStateApiProvider>
);

export default GuideContextProvider;
